import { ChevronDown, LogOut } from 'lucide-react';

import { useNativeLogout } from '@/entities/auth';
import { toolsLinks } from '@/shared/config/tools';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/DropdownMenu';
import type { User } from '@/lib/api/users';
import { UserAvatar } from '@/pages/groups/UserAvatar';

import { dropdownContentClass } from './DesktopSidebar.styles';
import { useSidebarCollapsed } from './sidebarCollapse';

// В выпадашке шеврона оставляем только инструменты, которым нет места
// отдельным пунктом навбара.
const HEADER_TOOL_IDS = ['eds', 'time-report'];

type DesktopSidebarHeaderProps = {
  avatarOpen: boolean;
  user?: User;
  onAvatarOpenChange: (open: boolean) => void;
  onNavigate: (to: string) => void;
};

export function DesktopSidebarHeader({
  avatarOpen,
  user,
  onAvatarOpenChange,
  onNavigate,
}: DesktopSidebarHeaderProps) {
  const { mutate: nativeLogout } = useNativeLogout();
  const collapsed = useSidebarCollapsed();

  const avatar = user ? (
    <UserAvatar user={user} size="lg" />
  ) : (
    <div className="size-8 shrink-0 animate-pulse rounded-full bg-gray-200" />
  );

  return (
    <DropdownMenu open={avatarOpen} onOpenChange={onAvatarOpenChange}>
      <DropdownMenuTrigger asChild>
        {collapsed ? (
          // Свёрнутый сайдбар: только аватар, меню — то же.
          <button
            type="button"
            aria-label="Профиль и выход"
            className="flex size-[40px] items-center justify-center rounded-[12px] bg-[#f9f9fd] shadow-[0_8px_22px_rgba(17,24,39,0.035)] transition-colors hover:bg-[#F1F3F8] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#102653]/20"
          >
            {avatar}
          </button>
        ) : (
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-[12px] bg-[#f9f9fd] px-[11px] py-[10px] text-left shadow-[0_8px_22px_rgba(17,24,39,0.035)] transition-colors hover:bg-[#F8F9FB] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#102653]/20 h-[40px]"
          >
            {avatar}
            <span className="font-normal leading-none text-[#B58E60] text-[clamp(14px,1.9vh,17px)]">
              DOIT
            </span>
            <ChevronDown
              className="ml-auto size-5 text-[#313846]"
              strokeWidth={1.8}
            />
          </button>
        )}
      </DropdownMenuTrigger>
      {/* Развёрнутый: меню под кнопкой и шириной с неё. Свёрнутый: кнопка
          в 40px — меню открываем вбок своей шириной. */}
      <DropdownMenuContent
        side={collapsed ? 'right' : 'bottom'}
        align="start"
        className={
          collapsed
            ? `${dropdownContentClass} w-[220px]`
            : `${dropdownContentClass} w-[var(--radix-dropdown-menu-trigger-width)] min-w-[var(--radix-dropdown-menu-trigger-width)]`
        }
      >
        <DropdownMenuItem
          className="cursor-pointer text-[calc(13px*var(--nav-scale,1))] font-normal focus:bg-[#F6F7FA] focus:text-[#17244A]"
          onSelect={() => onNavigate('/profile')}
        >
          Профиль
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer text-[calc(13px*var(--nav-scale,1))] font-normal focus:bg-[#F6F7FA] focus:text-[#17244A]"
          onSelect={() => onNavigate('/notifications')}
        >
          Уведомления
        </DropdownMenuItem>
        <div className="my-1 border-t border-[#E7EAF0]" />
        {toolsLinks
          .filter((item) => HEADER_TOOL_IDS.includes(item.id))
          .map((item) => (
            <DropdownMenuItem
              key={item.id}
              className="cursor-pointer text-[calc(13px*var(--nav-scale,1))] font-normal focus:bg-[#F6F7FA] focus:text-[#17244A]"
              onSelect={() => onNavigate(`/tools/${item.id}`)}
            >
              {item.label}
            </DropdownMenuItem>
          ))}
        <div className="my-1 border-t border-[#E7EAF0]" />
        <DropdownMenuItem
          className="cursor-pointer gap-2 text-[calc(13px*var(--nav-scale,1))] font-normal text-red-600 focus:bg-red-50 focus:text-red-600"
          onSelect={() => nativeLogout()}
        >
          <LogOut className="size-4" strokeWidth={1.8} />
          Выйти
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
